import {useCallback, useEffect, useMemo, useState} from 'react';
import axios from 'axios';
import {Button} from '@/components/ui/button';
import {Input} from '@/components/ui/input';
import {Switch} from '@/components/ui/switch';
import {Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription} from '@/components/ui/dialog';
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select';
import {toast} from 'sonner';
import {Loader2, Search, RotateCcw, BadgePercent} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT = 'DEFAULT'; // no category rule → restaurant commission applies

const ruleLabel = (type, value) =>
    type === 'FLAT' ? `₹${Number(value || 0).toLocaleString('en-IN')} flat` : `${Number(value || 0)}%`;

const valueError = (type, raw) => {
    if (type === DEFAULT) return null;
    if (raw === '' || raw === null || raw === undefined) return 'Enter a value';
    const v = Number(raw);
    if (!Number.isFinite(v) || v < 0) return 'Invalid value';
    if (type === 'PERCENTAGE' && v > 100) return 'Max 100%';
    if (type === 'FLAT' && v > 10000) return 'Max ₹10,000';
    return null;
};

/**
 * Category-wise commission for ONE restaurant (Commission Management rules).
 * Each row = one menu category / sub-category. "Restaurant default" means no
 * rule: items fall back to the restaurant's own commission.
 *
 * Props:
 *   open, onOpenChange, restaurantId
 *   restaurantCommission { type, value }   shown as the fallback
 *   itemCounts { [categoryId]: number }     optional, shown per row
 *   focusCategoryId                         optional, pre-filters to that category
 *   onSaved()                               called after a successful save
 */
const CategoryCommissionDialog = ({
                                      open,
                                      onOpenChange,
                                      restaurantId,
                                      restaurantCommission,
                                      itemCounts = {},
                                      focusCategoryId = null,
                                      onSaved
                                  }) => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [rows, setRows] = useState([]);       // [{ id, label, parentId, active, original, draft }]
    const [search, setSearch] = useState('');
    const [onlyFocus, setOnlyFocus] = useState(null);
    const [serverErrors, setServerErrors] = useState({}); // categoryId -> message

    const load = useCallback(async () => {
        if (!restaurantId) return;
        setLoading(true);
        setServerErrors({});
        try {
            // Rules (all statuses) — paged, 200 per page.
            const fetchRules = async () => {
                const all = [];
                for (let page = 1; page <= 20; page++) {
                    const res = await axios.get(`${API}/commissions`, {params: {restaurantId, limit: 200, page}});
                    all.push(...(res.data?.data || []));
                    if (page >= (res.data?.pagination?.totalPages || 1)) break;
                }
                return all;
            };
            const [catRes, rules] = await Promise.all([
                axios.get(`${API}/categories`, {params: {resturantId: restaurantId, limit: 1000}}),
                fetchRules()
            ]);
            const cats = (Array.isArray(catRes.data?.data) ? catRes.data.data : []).filter((c) => !c.isDeleted);
            const byId = new Map(cats.map((c) => [String(c.id), c]));
            const ruleByCat = new Map(rules.map((r) => [String(r.categoryId), r]));

            const built = cats.map((c) => {
                const id = String(c.id);
                const parentId = c.parentId ? String(c.parentId._id || c.parentId) : null;
                const parent = parentId ? byId.get(parentId) : null;
                const rule = ruleByCat.get(id);
                const original = rule
                    ? {type: rule.commissionType, value: String(rule.commissionValue ?? ''), status: rule.status !== false}
                    : {type: DEFAULT, value: '', status: true};
                return {
                    id,
                    parentId,
                    label: parent ? `${parent.name} › ${c.name}` : c.name,
                    sortKey: `${parent ? parent.name : c.name}\u0000${parent ? c.name : ''}`,
                    active: c.status !== false,
                    original,
                    draft: {...original}
                };
            }).sort((a, b) => a.sortKey.localeCompare(b.sortKey));
            setRows(built);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load category commissions');
        } finally {
            setLoading(false);
        }
    }, [restaurantId]);

    useEffect(() => {
        if (open) {
            setSearch('');
            setOnlyFocus(focusCategoryId ? String(focusCategoryId) : null);
            load();
        }
    }, [open, focusCategoryId, load]);

    const isDirty = (r) =>
        r.draft.type !== r.original.type ||
        (r.draft.type !== DEFAULT && (
            Number(r.draft.value) !== Number(r.original.value) ||
            r.draft.status !== r.original.status
        ));

    const dirtyRows = useMemo(() => rows.filter(isDirty), [rows]);
    const invalidRows = useMemo(
        () => dirtyRows.filter((r) => valueError(r.draft.type, r.draft.value)),
        [dirtyRows]
    );

    const visibleRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        return rows.filter((r) => {
            if (onlyFocus && r.id !== onlyFocus && r.parentId !== onlyFocus) return false;
            if (q && !r.label.toLowerCase().includes(q)) return false;
            return true;
        });
    }, [rows, search, onlyFocus]);

    const setDraft = (id, patch) => {
        setRows((prev) => prev.map((r) => (r.id === id ? {...r, draft: {...r.draft, ...patch}} : r)));
        setServerErrors((e) => {
            if (!e[id]) return e;
            const n = {...e};
            delete n[id];
            return n;
        });
    };

    // Apply one type/value to every visible row (e.g. all sub-categories of a category).
    const [applyType, setApplyType] = useState('PERCENTAGE');
    const [applyValue, setApplyValue] = useState('');
    const applyToVisible = () => {
        const err = valueError(applyType, applyValue);
        if (err) return toast.error(err);
        const ids = new Set(visibleRows.map((r) => r.id));
        setRows((prev) => prev.map((r) => (ids.has(r.id)
            ? {...r, draft: {type: applyType, value: applyType === DEFAULT ? '' : String(applyValue), status: true}}
            : r)));
        toast.success(`Applied to ${ids.size} categor${ids.size === 1 ? 'y' : 'ies'} — click Save to confirm`);
    };

    const save = async () => {
        if (!dirtyRows.length) return toast.info('No changes to save');
        if (invalidRows.length) return toast.error(`Fix the value for "${invalidRows[0].label}"`);
        setSaving(true);
        setServerErrors({});
        try {
            const payload = dirtyRows.map((r) => (r.draft.type === DEFAULT
                ? {categoryId: r.id, remove: true}
                : {
                    categoryId: r.id,
                    commissionType: r.draft.type,
                    commissionValue: Number(r.draft.value),
                    status: r.draft.status
                }));
            const res = await axios.put(`${API}/commissions/restaurant/${restaurantId}`, {rules: payload});
            const s = res.data?.summary || {};
            toast.success(`${res.data?.message || 'Saved'} — ${s.created || 0} new, ${s.updated || 0} updated, ${s.removed || 0} reset`);
            setRows((prev) => prev.map((r) => ({...r, original: {...r.draft, value: r.draft.type === DEFAULT ? '' : r.draft.value}})));
            onSaved && onSaved();
        } catch (err) {
            const d = err.response?.data || {};
            if (Array.isArray(d.errors)) {
                setServerErrors(Object.fromEntries(d.errors.map((e) => [String(e.categoryId), e.message])));
            }
            toast.error(d.detail || 'Failed to save category commissions');
        } finally {
            setSaving(false);
        }
    };

    const close = (o) => {
        if (saving) return;
        if (!o && dirtyRows.length && !window.confirm('You have unsaved commission changes. Close without saving?')) return;
        onOpenChange(o);
    };

    const fallback = restaurantCommission?.type && Number(restaurantCommission?.value)
        ? ruleLabel(String(restaurantCommission.type).toUpperCase() === 'FLAT' || String(restaurantCommission.type).toUpperCase() === 'FIXED' ? 'FLAT' : 'PERCENTAGE', restaurantCommission.value)
        : 'No commission';
    const focusLabel = onlyFocus ? rows.find((r) => r.id === onlyFocus)?.label : null;

    return (
        <Dialog open={open} onOpenChange={close}>
            <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col" data-testid="category-commission-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <BadgePercent size={20} className="text-orange-600"/> Category commission
                    </DialogTitle>
                    <DialogDescription>
                        Set a commission for each category. Items use their sub-category rule, then their category rule,
                        otherwise the restaurant commission (<b>{fallback}</b>).
                    </DialogDescription>
                </DialogHeader>

                {/* Toolbar */}
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div className="flex items-center gap-2">
                        <div className="relative w-full md:w-64">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
                            <Input className="pl-9" placeholder="Search category" value={search}
                                   onChange={(e) => setSearch(e.target.value)}/>
                        </div>
                        {onlyFocus && (
                            <Button type="button" variant="outline" size="sm" onClick={() => setOnlyFocus(null)}>
                                Show all categories
                            </Button>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 whitespace-nowrap">Set all shown:</span>
                        <Select value={applyType} onValueChange={setApplyType}>
                            <SelectTrigger className="w-36 h-9"><SelectValue/></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="PERCENTAGE">Percentage</SelectItem>
                                <SelectItem value="FLAT">Flat (₹)</SelectItem>
                                <SelectItem value={DEFAULT}>Restaurant default</SelectItem>
                            </SelectContent>
                        </Select>
                        <Input type="number" min="0" step="0.01" className="w-24 h-9" placeholder="Value"
                               disabled={applyType === DEFAULT} value={applyType === DEFAULT ? '' : applyValue}
                               onChange={(e) => setApplyValue(e.target.value)}/>
                        <Button type="button" size="sm" variant="outline" onClick={applyToVisible}
                                disabled={!visibleRows.length}>Apply</Button>
                    </div>
                </div>
                {focusLabel && (
                    <div className="text-xs text-gray-500 -mt-1">Showing <b>{focusLabel}</b> and its sub-categories.</div>
                )}

                {/* Rows */}
                <div className="flex-1 overflow-auto rounded-lg border">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500 sticky top-0 z-10">
                        <tr>
                            <th className="px-3 py-2 font-medium">Category</th>
                            <th className="px-3 py-2 font-medium w-44">Commission type</th>
                            <th className="px-3 py-2 font-medium w-32">Value</th>
                            <th className="px-3 py-2 font-medium w-28">Status</th>
                            <th className="px-3 py-2 w-10"></th>
                        </tr>
                        </thead>
                        <tbody className="divide-y">
                        {loading ? (
                            [1, 2, 3, 4].map((i) => (
                                <tr key={i}><td colSpan={5} className="px-3 py-2"><div className="h-8 rounded bg-gray-100 animate-pulse"/></td></tr>
                            ))
                        ) : visibleRows.length === 0 ? (
                            <tr><td colSpan={5} className="px-3 py-10 text-center text-gray-500">
                                {rows.length ? 'No category matches your search' : 'This restaurant has no categories yet'}
                            </td></tr>
                        ) : visibleRows.map((r) => {
                            const dirty = isDirty(r);
                            const err = (dirty && valueError(r.draft.type, r.draft.value)) || serverErrors[r.id];
                            const isDefault = r.draft.type === DEFAULT;
                            const count = itemCounts[r.id];
                            return (
                                <tr key={r.id} className={dirty ? 'bg-orange-50/50' : ''} data-testid={`cat-commission-row-${r.id}`}>
                                    <td className="px-3 py-2">
                                        <div className={`font-medium ${r.parentId ? 'pl-4 text-gray-700' : 'text-gray-900'}`}>
                                            {r.label}
                                        </div>
                                        <div className="text-xs text-gray-500 flex flex-wrap gap-x-2">
                                            {count !== undefined && <span>{count} item{count === 1 ? '' : 's'}</span>}
                                            {!r.active && <span className="text-amber-700">Category inactive</span>}
                                            {r.original.type !== DEFAULT
                                                ? <span>Current: {ruleLabel(r.original.type, r.original.value)}{r.original.status ? '' : ' (off)'}</span>
                                                : <span>Current: restaurant default</span>}
                                        </div>
                                    </td>
                                    <td className="px-3 py-2">
                                        <Select value={r.draft.type}
                                                onValueChange={(v) => setDraft(r.id, {type: v, value: v === DEFAULT ? '' : r.draft.value})}>
                                            <SelectTrigger className="h-9"><SelectValue/></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value={DEFAULT}>Restaurant default</SelectItem>
                                                <SelectItem value="PERCENTAGE">Percentage</SelectItem>
                                                <SelectItem value="FLAT">Flat (₹)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="relative">
                                            {r.draft.type === 'FLAT' && <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 text-sm">₹</span>}
                                            <Input type="number" min="0" step="0.01"
                                                   className={`h-9 ${r.draft.type === 'FLAT' ? 'pl-6' : ''} ${r.draft.type === 'PERCENTAGE' ? 'pr-6' : ''} ${err ? 'border-red-500' : ''}`}
                                                   disabled={isDefault} placeholder={isDefault ? '—' : '0'}
                                                   value={r.draft.value}
                                                   onChange={(e) => setDraft(r.id, {value: e.target.value})}/>
                                            {r.draft.type === 'PERCENTAGE' && <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>}
                                        </div>
                                        {err && <div className="text-xs text-red-600 mt-0.5">{err}</div>}
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="flex items-center gap-2">
                                            <Switch checked={!isDefault && r.draft.status} disabled={isDefault}
                                                    onCheckedChange={(v) => setDraft(r.id, {status: v})}
                                                    className="data-[state=checked]:bg-green-600"/>
                                            <span className="text-xs text-gray-500">{isDefault ? '—' : r.draft.status ? 'Active' : 'Off'}</span>
                                        </div>
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        {dirty && (
                                            <Button type="button" size="sm" variant="ghost" title="Undo changes"
                                                    onClick={() => setDraft(r.id, {...r.original})}>
                                                <RotateCcw size={14}/>
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                </div>

                <div className="flex items-center justify-between gap-2 pt-1">
                    <span className="text-xs text-gray-500">
                        {dirtyRows.length ? `${dirtyRows.length} unsaved change${dirtyRows.length === 1 ? '' : 's'}` : 'No changes'}
                    </span>
                    <div className="flex gap-2">
                        <Button type="button" variant="outline" disabled={saving} onClick={() => close(false)}>Close</Button>
                        <Button type="button" onClick={save} disabled={saving || loading || !dirtyRows.length || invalidRows.length > 0}
                                style={{backgroundColor: '#C2410C'}} data-testid="category-commission-save">
                            {saving && <Loader2 size={16} className="mr-2 animate-spin"/>}Save changes
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default CategoryCommissionDialog;
